import { DealStatusAPI } from '@business/api/deal';
import { DealStatusId, DealStatusResponse } from '@business/entities/deal';
import { DealStatusData } from '@business/api/deal/deal_status_api';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { map, switchMap } from 'rxjs/operators';

export class DealStatusBloc extends BaseBloc {
  private readonly statusRepo = new Repository<DealStatusResponse>({
    getItemId: item => item.id,
  });

  readonly items$ = this.statusRepo.items$.pipe(map(items => R.sortBy(items.filter(i => !i.deletedAt), 'menuOrder')));
  readonly itemsWithTrashed$ = this.statusRepo.items$.pipe(map(items => R.sortBy(items, 'menuOrder')));
  readonly selectItem = this.statusRepo.selectItem;

  onReset() {
    this.statusRepo.reset();
  }

  fetchDealStatuses = (withTrashed = false) => {
    return DealStatusAPI.fetchDealStatuses(withTrashed).pipe(
      this.statusRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_deal_statuses_error') }),
    );
  };

  createDealStatus = (data: DealStatusData) => {
    return DealStatusAPI.createDealStatus(data).pipe(
      this.statusRepo.ops.reset(),
      handleMessage({ type: requestMessage('create_deal_status') }),
    );
  };

  updateDealStatus = (id: DealStatusId, data: DealStatusData) => {
    return DealStatusAPI.updateDealStatus(id, data).pipe(
      this.statusRepo.ops.reset(),
      handleMessage({ type: requestMessage('update_deal_status') }),
    );
  };

  deleteDealStatus = (id: DealStatusId) => {
    return showDeleteConfirmation('Delete Status', 'Do you really want to remove this status?').pipe(
      switchMap(() => DealStatusAPI.deleteDealStatus(id)),
      this.statusRepo.ops.removeOne(() => id),
      handleMessage({ type: requestMessage('delete_deal_status') }),
    );
  };

  restoreDealStatus = (id: DealStatusId) => {
    return DealStatusAPI.restoreDealStatus(id).pipe(
      this.statusRepo.ops.reset(),
      handleMessage({ type: requestMessage('restore_deal_status') }),
    );
  };

  sortDealStatuses = (ids: DealStatusId[]) => {
    return DealStatusAPI.sortDealStatuses(ids).pipe(
      this.statusRepo.ops.reset(),
      handleMessage({ type: requestMessage('update_deal_status') }),
    );
  };
}
